/* eslint-disable no-underscore-dangle */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getValue, pathComparator, parentPath } from '@truqc/content-path';
import Lightbox from '../../common/lightbox';
import PDF from '../../common/pdf/pdf';
import { labelSelector, newPathSelector, valueSelector } from '../selectors/index';
import entityComposition from '../../common/classes/entityComposition';
import { getContent } from '../selectors/simpleSelectors';
import Label from './label';
import * as ajax from '../../../actions/ajax';
import store from '../../../reducers/storeRegistration';
import loginActions from '../../../actions/loginActionCreators';

// Helper functions for file type detection
const isImageFile = downloadUrl => downloadUrl === null || downloadUrl === void 0 ? void 0 : downloadUrl.match(/\.(jpg|jpeg|png|gif)$/i);
const isVideoFile = downloadUrl => downloadUrl === null || downloadUrl === void 0 ? void 0 : downloadUrl.match(/\.(mp4|webm|ogg|mov|avi)$/i);
const isPdfFile = downloadUrl => downloadUrl === null || downloadUrl === void 0 ? void 0 : downloadUrl.includes('pdf');
const MediaViewer = ({
  originalUploadType: _originalUploadType = null,
  value: _value = {},
  label,
  content,
  path
}) => {
  const fileUrl = (_value === null || _value === void 0 ? void 0 : _value._cache) || (_value === null || _value === void 0 ? void 0 : _value._download);

  // Fallback .mov support for transcoded mp4 video -- --
  // Check if the parentPath(path) has originalUploadType === 'mov'
  const [effectiveFileUrl, setEffectiveFileUrl] = useState(fileUrl);
  useEffect(() => {
    let isMounted = true;
    async function fetchMovUrl() {
      var _value$_download;
      // Only proceed if this is a MOV upload that was transcoded to MP4
      if (_originalUploadType === 'mov' && _value !== null && _value !== void 0 && (_value$_download = _value._download) !== null && _value$_download !== void 0 && _value$_download.match(/\.mp4$/i)) {
        // First, check if the MP4 URL is accessible
        try {
          const response = await fetch(fileUrl, {
            method: 'HEAD'
          });
          if (response.ok) {
            // MP4 exists and is accessible, use it
            if (isMounted) setEffectiveFileUrl(fileUrl);
            return;
          }
        } catch (_unused) {
          // MP4 fetch failed, fall through to try MOV
        }

        // MP4 is not accessible, try to get the MOV URL
        const fileRemotePath = _value._download.replace(/\.mp4$/i, '.mov');
        const fetchDownloadUrlAndSetEffectiveFileUrl = async fileName => {
          const {
            data: downloadURL
          } = await ajax.get('/files/downloadUrl', {
            fileName
          }, 'text');
          if (downloadURL && isMounted) setEffectiveFileUrl(downloadURL);
        };
        try {
          await fetchDownloadUrlAndSetEffectiveFileUrl(fileRemotePath);
        } catch (e) {
          var _e$response;
          // If token expired (401), refresh and retry once
          if (((_e$response = e.response) === null || _e$response === void 0 ? void 0 : _e$response.status) === 401) {
            try {
              // if the error was a 401, refresh the token and retry
              await store.dispatch(loginActions.serverToken());
              await fetchDownloadUrlAndSetEffectiveFileUrl(fileRemotePath);
              return; // On successful retry, don't fall through to fallback
            } catch (_unused2) {
              // Retry also failed, fall through to fallback
            }
          }
          // Non-token error or retry failed, fallback to original fileUrl
          if (isMounted) setEffectiveFileUrl(fileUrl);
        }
      }
    }
    fetchMovUrl();
    return () => {
      isMounted = false;
    };
  }, [_originalUploadType, _value === null || _value === void 0 ? void 0 : _value._download, fileUrl]);
  if (!fileUrl) return null;
  if (isImageFile(_value === null || _value === void 0 ? void 0 : _value._download)) {
    // Lightbox boilerplate to build a list of sibling images in the same collection
    const imageParentContentPath = parentPath(path);
    const imageSiblings = getValue(imageParentContentPath, content);
    const images = (Object.values(imageSiblings || {}) || []).sort(pathComparator('_created')).filter(n => isImageFile(n.remotePath._download)).filter(n => n.remotePath._downloadURL || n.remotePath._cache).map(n => n.remotePath._downloadURL || n.remotePath._cache);
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Label, {
      value: label
    }), /*#__PURE__*/React.createElement(Lightbox, {
      selectedIndex: images.indexOf(fileUrl),
      images: images,
      style: {
        width: '100%',
        marginBottom: '0px'
      },
      enableZoom: true
    }));
  }
  if (isVideoFile(_value === null || _value === void 0 ? void 0 : _value._download)) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Label, {
      value: label
    }), /*#__PURE__*/React.createElement("video", {
      src: effectiveFileUrl,
      controls: true,
      style: {
        width: '100%',
        height: 'auto'
      },
      crossOrigin: "anonymous"
      // type="video/mp4,video/webm,video/ogg,video/quicktime,video/x-msvideo,video/avi"
    }, /*#__PURE__*/React.createElement("track", {
      kind: "captions"
    }), "Your browser does not support the video tag."));
  }
  if (isPdfFile(_value === null || _value === void 0 ? void 0 : _value._download)) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Label, {
      value: label
    }), /*#__PURE__*/React.createElement(PDF, {
      src: fileUrl,
      title: label,
      hideTitleIfMobile: true,
      nativeRenderer: true
    }));
  }

  // could detect file time from _download
  return /*#__PURE__*/React.createElement("p", null, "Unable to determine file type");
};
MediaViewer.propTypes = {
  originalUploadType: PropTypes.string,
  path: PropTypes.string.isRequired,
  value: PropTypes.shape({
    _cache: PropTypes.string,
    _download: PropTypes.string,
    contentType: PropTypes.string,
    type: PropTypes.string,
    name: PropTypes.string
  }),
  label: PropTypes.string.isRequired,
  content: PropTypes.shape({}).isRequired
};
export { MediaViewer as UncomposedMediaViewer };
export default entityComposition(MediaViewer, [], {
  label: labelSelector,
  value: valueSelector,
  content: getContent,
  path: newPathSelector
});